/**
 * Returns a function that as long as it continues to be invoked, won't be triggered.
 * @param {Function} fn - Callback function.
 * @param {number} [wait=300] - Delay (in milliseconds).
 * @returns {Function}
 */
function debounce(fn, wait = 300) {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, args), wait);
  };
}

/**
 * Keeps the product sticky sidebar working with native position:sticky. Does two things:
 * 1. Sets --product-sticky-top on the parent so the sticky sits just below the header,
 *    and updates it when the header expands/collapses (no gap).
 * 2. Sets --sticky-height on the parent so the column has correct min-height.
 * No scroll-direction logic — the sidebar uses native sticky and can scroll out of view.
 */
if (!customElements.get('sticky-scroll-direction')) {
  class StickyScrollDirection extends HTMLElement {
    connectedCallback() {
      this.init();
    }

    init() {
      this.storeHeader = document.querySelector('store-header[data-is-sticky="true"]');
      this.container = this.firstElementChild;
      this.minStickySize = this.dataset.minStickySize;

      this.toggleListeners();
      window.addEventListener('on:breakpoint-change', this.toggleListeners.bind(this));
    }

    /**
     * Returns the current top offset (px) so the sticky sits just below the header.
     * Uses the live header position so it matches when the header expands/collapses.
     */
    getStickyTopOffset() {
      if (this.storeHeader) {
        const rect = this.storeHeader.getBoundingClientRect();
        const padding = (typeof theme !== 'undefined' && theme.mediaMatches && theme.mediaMatches.md) ? 24 : 12;
        return Math.round(rect.bottom + padding);
      }
      const padded = getComputedStyle(document.documentElement).getPropertyValue('--header-end-padded').trim();
      if (padded) {
        const num = parseFloat(padded);
        if (!Number.isNaN(num)) return num;
      }
      return 48;
    }

    /**
     * Updates the CSS variable that controls where the sticky sticks. Parent is .product-info.
     */
    updateStickyTop() {
      if (this.parentElement) {
        this.parentElement.style.setProperty('--product-sticky-top', `${this.getStickyTopOffset()}px`);
      }
      if (this._directionalHandler) this._directionalHandler();
    }

    /**
     * When the header expands or collapses, update the sticky top so it follows without a gap.
     */
    bindHeaderTransitionEnd() {
      if (!this.storeHeader) return;
      this._onHeaderTransitionEnd = () => {
        this.updateStickyTop();
        this._stopRafUpdates();
      };
      this.storeHeader.addEventListener('transitionend', this._onHeaderTransitionEnd);

      const transitionMs = 350;
      this._headerWasOut = this.storeHeader.classList.contains('is-out');
      this._onHeaderClassChange = () => {
        const isOut = this.storeHeader.classList.contains('is-out');
        if (isOut === this._headerWasOut) return;
        this._headerWasOut = isOut;
        this.updateStickyTop();
        this._stopRafUpdates();
        const start = performance.now();
        const tick = () => {
          if (performance.now() - start >= transitionMs) return;
          this.updateStickyTop();
          this._rafId = requestAnimationFrame(tick);
        };
        this._rafId = requestAnimationFrame(tick);
      };
      this._headerObserver = new MutationObserver(this._onHeaderClassChange);
      this._headerObserver.observe(this.storeHeader, { attributes: true, attributeFilter: ['class'] });
    }

    _stopRafUpdates() {
      if (this._rafId != null) {
        cancelAnimationFrame(this._rafId);
        this._rafId = null;
      }
    }

    unbindHeaderTransitionEnd() {
      this._stopRafUpdates();
      if (!this.storeHeader) return;
      if (this._onHeaderTransitionEnd) {
        this.storeHeader.removeEventListener('transitionend', this._onHeaderTransitionEnd);
      }
      if (this._headerObserver) {
        this._headerObserver.disconnect();
      }
    }

    toggleListeners() {
      if ((this.minStickySize === 'lg' && theme.mediaMatches.lg)
        || (this.minStickySize === 'md' && theme.mediaMatches.md)) {
        this.addListeners();
      } else {
        this.removeListeners();
      }
    }

    addListeners() {
      this.bindHeaderTransitionEnd();
      this.updateStickyTop();

      // Keep sticky top in sync on scroll (e.g. for native sticky; header may change)
      this._scrollHandler = () => this.updateStickyTop();
      window.addEventListener('scroll', this._scrollHandler, { passive: true });

      // Opt-in (data-scroll-direction="true", used by facet filters): a sidebar taller than the
      // viewport scrolls along with the page until its bottom is visible, then sticks there;
      // scrolling up brings the top back under the header. The product page doesn't opt in.
      if (this.dataset.scrollDirection === 'true' && !this._directionalHandler) {
        this.currentTop = this.getStickyTopOffset();
        this.lastScrollY = window.scrollY;
        this._directionalHandler = () => {
          if (this._directionalRaf) return;
          this._directionalRaf = requestAnimationFrame(() => {
            this._directionalRaf = null;
            this.handleDirectionalScroll();
          });
        };
        window.addEventListener('scroll', this._directionalHandler, { passive: true });
        window.addEventListener('resize', this._directionalHandler);
        if ('ResizeObserver' in window) {
          this._containerResizeObserver = new ResizeObserver(this._directionalHandler);
          this._containerResizeObserver.observe(this.container);
        }
        this.handleDirectionalScroll();
      }

      if (this.parentElement.dataset.stickyHeightElems) {
        this.debouncedSetStickyHeights = this.debouncedSetStickyHeights
          || debounce(this.setStickyHeight.bind(this), 500);
        window.addEventListener('resize', this.debouncedSetStickyHeights);

        if ('MutationObserver' in window) {
          this.stickyHeightElems = this.parentElement.dataset.stickyHeightElems;
          this.observer = new MutationObserver(this.debouncedSetStickyHeights);
          document.querySelectorAll(this.stickyHeightElems).forEach((elem) => {
            this.observer.observe(elem, {
              childList: true,
              attributes: true,
              subtree: true
            });
          });
        }
        this.setStickyHeight();
      }

      this.disclosures = this.querySelectorAll('details');
      if (this.disclosures) {
        this.disclosureChangeHandler = this.disclosureChangeHandler
          || this.handleDisclosureChange.bind(this);
        this.disclosures.forEach((disclosure) => {
          disclosure.addEventListener('transitionend', this.disclosureChangeHandler);
        });
      }
    }

    handleDisclosureChange(evt) {
      if (evt.target.classList.contains('disclosure__panel')) {
        const summaryElem = evt.target.closest('.disclosure').querySelector('summary');
        if (!theme.elementUtil.isInViewport(summaryElem)) {
          const headerOffset = this.getStickyTopOffset();
          window.scrollTo({
            behavior: 'smooth',
            top: summaryElem.getBoundingClientRect().top
              - document.body.getBoundingClientRect().top - headerOffset - 20
          });
        }
      }
    }

    /**
     * Moves the sidebar's top by the scroll delta, clamped between "top under the header" and
     * "bottom just above the viewport bottom". A sidebar shorter than the viewport stays at the top.
     */
    handleDirectionalScroll() {
      const topOffset = this.getStickyTopOffset();
      const minTop = Math.min(topOffset, window.innerHeight - this.container.offsetHeight - 24);
      const delta = window.scrollY - this.lastScrollY;
      // Already pinned under the header: stay pinned while scrolling up or while the header slides
      // in/out (scroll anchoring nudges scrollY during the header animation)
      const lastTopOffset = this.lastTopOffset ?? topOffset;
      const wasPinned = this.currentTop >= lastTopOffset - 1;
      this.currentTop = wasPinned && (delta <= 0 || topOffset !== lastTopOffset)
        ? topOffset
        : this.currentTop - delta;
      this.currentTop = Math.min(topOffset, Math.max(minTop, this.currentTop));
      this.lastScrollY = window.scrollY;
      this.lastTopOffset = topOffset;
      this.container.style.top = `${Math.round(this.currentTop)}px`;
    }

    removeListeners() {
      this.unbindHeaderTransitionEnd();
      window.removeEventListener('scroll', this._scrollHandler);
      if (this._directionalHandler) {
        window.removeEventListener('scroll', this._directionalHandler);
        window.removeEventListener('resize', this._directionalHandler);
        if (this._containerResizeObserver) this._containerResizeObserver.disconnect();
        if (this._directionalRaf) cancelAnimationFrame(this._directionalRaf);
        this._directionalRaf = null;
        this._directionalHandler = null;
      }
      if (this.container && this.container.style) {
        this.container.style.top = '';
      }
      if (this.parentElement) {
        this.parentElement.style.removeProperty('--product-sticky-top');
      }
      if (this.observer) {
        this.observer.disconnect();
        window.removeEventListener('resize', this.debouncedSetStickyHeights);
      }
      if (this.disclosures) {
        this.disclosures.forEach((disclosure) => {
          disclosure.removeEventListener('transitionend', this.disclosureChangeHandler);
        });
      }
    }

    disconnectedCallback() {
      this.removeListeners();
    }

    setStickyHeight() {
      let totalHeight = 0;
      document.querySelectorAll(this.stickyHeightElems).forEach((elem) => {
        totalHeight += elem.getBoundingClientRect().height;
      });
      this.parentElement.style.setProperty('--sticky-height', `${parseInt(totalHeight, 10)}px`);
    }
  }

  customElements.define('sticky-scroll-direction', StickyScrollDirection);
}
